/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Spinner,
  Tabs,
  Tab,
  ToastContainer,
  Toast,
} from 'react-bootstrap';
import { supabase } from '../../lib/supabase';
import type { Organisation, SupplierCompanyProfile } from '../../types/types';
import { captureSupabaseError } from '../../lib/sentry';
import { SupplierProfileBuilderCertifications } from './SupplierProfileBuilderCertifications';
import { SupplierProfileBuilderPolicies } from './SupplierProfileBuilderPolicies';
import SupplierProfileBuilderGeneralTab from './SupplierProfileBuilderGeneralTab';
import SupplierProfileBuilderOperationsTab from './SupplierProfileBuilderOperationsTab';
import SupplierProfileBuilderRestrictedTab from './SupplierProfileBuilderRestrictedTab';
import { gToastPosition, gToastSentiment } from '../../util/constst';

interface SupplierProfileBuilderProps {
  supplierId: string;
  show: boolean;
  onHide: () => void;
  organisation?: Organisation;
}

interface RestrictedFieldsSet {
  ubo_details: string;
  credit_score_value: string;
  credit_score_provider: string;
  is_financials_public: boolean;
  last_financial_audit_date?: string;
}

const SupplierProfileBuilder: React.FC<SupplierProfileBuilderProps> = ({
  supplierId,
  show,
  onHide,
  organisation,
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [changesSaved, setChangesSaved] = useState(true);
  // NEW: Restricted Fields State
  const [restrictedFields, setRestrictedFields] = useState<RestrictedFieldsSet>(
    {
      ubo_details: '',
      credit_score_value: '',
      credit_score_provider: '',
      is_financials_public: false,
      last_financial_audit_date: undefined,
    }
  );

  // Data States
  const [profile, setProfile] = useState<SupplierCompanyProfile>({
    supplier_id: supplierId,
    company_name: organisation?.name || '',
    registration_number: organisation?.registration_number || '',
    display_assesment_results: false,
    tier_1_visibility_level: 'unknown',
    tier_2_visibility_level: 'none',
    raw_material_origins: [],
  });
  const [operations, setOperations] = useState<any>({
    supplier_id: supplierId,
  });
  const [opCountries, setOpCountries] = useState<number[]>([]);
  const [countries, setCountries] = useState<
    { id: number; name: string; code: string }[]
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [tempCountry, setTempCountry] = useState<number | null>(null);
  const [tempOriginCountry, setTempOriginCountry] = useState<string>('');

  useEffect(() => {
    if (show) fetchData();
  }, [show, supplierId]);

  const fetchData = async () => {
    setIsLoading(true);

    const [profRes, opRes, countryRes, countriesRes, restrictedRes] =
      await Promise.all([
        supabase
          .from('supplier_company_profiles')
          .select('*')
          .eq('supplier_id', supplierId)
          .maybeSingle(),
        supabase
          .from('supplier_company_operations')
          .select('*')
          .eq('supplier_id', supplierId)
          .maybeSingle(),
        supabase
          .from('supplier_operation_countries')
          .select('country_id')
          .eq('supplier_id', supplierId),
        supabase
          .from('countries')
          .select('id, name, code')
          .order('name', { ascending: true }),
        // Fetch the restricted fields using the profile_id (which is profRes.data.id)
        // Since we need the ID first, we handle this slightly sequentially if needed,
        // but assuming the trigger created it, we can join or query by supplier_id lookup
        supabase
          .from('supplier_company_profile_restricted_fields')
          .select('*')
          .filter(
            'profile_id',
            'in',
            supabase
              .from('supplier_company_profiles')
              .select('id')
              .eq('supplier_id', supplierId)
          )
          .maybeSingle(),
      ]);

    if (profRes.data) setProfile(profRes.data);
    if (opRes.data) setOperations(opRes.data);
    if (countriesRes.data) setCountries(countriesRes.data);
    if (countryRes.data)
      setOpCountries(countryRes.data.map((c) => c.country_id));
    if (restrictedRes.data) setRestrictedFields(restrictedRes.data);

    setIsLoading(false);
  };

  const handleSaveRestricted = async () => {
    setIsSaving(true);
    const { error: restrictedError } = await supabase
      .from('supplier_company_profile_restricted_fields')
      .upsert(
        {
          ...restrictedFields,
          profile_id: profile.id,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'profile_id' }
      );

    const { error: supplyChainError } = await supabase
      .from('supplier_company_profiles')
      .update({
        tier_1_visibility_level: profile.tier_1_visibility_level || 'unknown',
        tier_2_visibility_level: profile.tier_2_visibility_level || 'none',
        raw_material_origins: profile.raw_material_origins || [],
        updated_at: new Date().toISOString(),
      })
      .eq('supplier_id', supplierId);

    if (restrictedError || supplyChainError) {
      if (restrictedError) {
        alert(`Error saving restricted fields: ${restrictedError.message}`);
        captureSupabaseError(restrictedError, { op: 'upsert restricted fields' });
      }
      if (supplyChainError) {
        alert(`Error saving supply chain: ${supplyChainError.message}`);
        captureSupabaseError(supplyChainError, {
          op: 'update supply chain fields',
        });
      }
    } else {
      setShowSuccessToast(true);
      setChangesSaved(true);
    }
    setIsSaving(false);
  };

  const handleSaveGeneral = async () => {
    setErrors({});

    if (!profile.company_name?.trim()) {
      setErrors((prev) => ({
        ...prev,
        company_name: 'Company name is required',
      }));
      setActiveTab('general');
      return;
    }
    if (!profile.registration_number?.trim()) {
      setErrors((prev) => ({
        ...prev,
        registration_number: 'Registration Number is required',
      }));
      setActiveTab('general');
      return;
    }
    if (!profile.headquarters_location) {
      setErrors((prev) => ({
        ...prev,
        headquarters_location: 'Country is required',
      }));
      setActiveTab('general');
      return;
    }
    if (!profile.industry_id) {
      setErrors((prev) => ({ ...prev, industry_id: 'Industry is required' }));
      setActiveTab('general');
      return;
    }

    setIsSaving(true);

    // We ensure supplier_id is the organisation.id passed from your parent component
    const { error } = await supabase.from('supplier_company_profiles').upsert(
      {
        ...profile,
        supplier_id: supplierId, // This must be the organisation.id
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'supplier_id' }
    );

    if (error) {
      alert(`Error: ${error.message}`);
      captureSupabaseError(error, { op: 'upsert supplier profile' });
    } else {
      setActiveTab('operations');
      setShowSuccessToast(true);
      setChangesSaved(true);
    }
    setIsSaving(false);
  };

  /// Operations Save Handler
  const saveOperations = async () => {
    setErrors({});
    if (!operations.employee_count) {
      setErrors((prev) => ({
        ...prev,
        employee_count: 'Employee count is required',
      }));
      setActiveTab('operations');
      return;
    }

    if (!operations.revenue_range) {
      setErrors((prev) => ({
        ...prev,
        revenue_range: 'Revenue range is required',
      }));
      setActiveTab('operations');
      return;
    }

    if (opCountries.length === 0) {
      setErrors((prev) => ({
        ...prev,
        opCountries: 'At least one country of operation is required',
      }));
      setActiveTab('operations');
      return;
    }

    setIsSaving(true);

    // Upsert operations data
    const { error: opError } = await supabase
      .from('supplier_company_operations')
      .upsert(
        {
          ...operations,
          supplier_id: supplierId,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'supplier_id' }
      );

    if (opError) {
      alert(`Error saving operations: ${opError.message}`);
      captureSupabaseError(opError, { op: 'upsert supplier operations' });
      setIsSaving(false);
      return;
    }

    // Delete existing operation countries for this supplier
    const { error: deleteError } = await supabase
      .from('supplier_operation_countries')
      .delete()
      .eq('supplier_id', supplierId)
      .not('country_id', 'in', `(${opCountries.join(',')})`);

    if (deleteError) {
      alert(`Error clearing old operation countries: ${deleteError.message}`);
      captureSupabaseError(deleteError, {
        op: 'delete old operation countries',
      });
      setIsSaving(false);
      return;
    }

    const newRows = opCountries.map((id) => ({
      supplier_id: supplierId,
      country_id: id, // Ensure this matches your SQL 'country_id'
    }));

    const { error: countryError } = await supabase
      .from('supplier_operation_countries')
      .upsert(newRows, {
        // No parentheses here, just the column names separated by a comma
        onConflict: 'supplier_id,country_id',
      });

    let profileError: { message: string } | null = null;
    if (profile?.founded_year !== undefined) {
      const { error } = await supabase
        .from('supplier_company_profiles')
        .update({
          founded_year: profile.founded_year,
          updated_at: new Date().toISOString(),
        })
        .eq('supplier_id', supplierId);
      profileError = error;
    }

    if (countryError || profileError) {
      if (profileError) {
        alert(`Error saving founded year: ${profileError.message}`);
        captureSupabaseError(profileError, {
          op: 'update supplier profile founded_year',
        });
      }
      if (countryError) {
        alert(`Error saving operation countries: ${countryError.message}`);
        captureSupabaseError(countryError, {
          op: 'upsert operation countries',
        });
      }
    } else {
      setActiveTab('certifications');
      setShowSuccessToast(true);
      setChangesSaved(true);
    }

    setIsSaving(false);
  };

  // Function to add a country to the list
  const addCountry = () => {
    if (tempCountry && !opCountries.includes(tempCountry)) {
      setOpCountries([...opCountries, tempCountry]);
      setTempCountry(null); // Reset selection
    }
  };

  // Function to remove a country from the list
  const removeCountry = (id: number) => {
    setOpCountries(opCountries.filter((c) => c !== id));
  };

  const closeModal = () => {
    if (!changesSaved) {
      if (
        window.confirm(
          'You have unsaved changes. Are you sure you want to close?'
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  const addOriginCountry = () => {
    if (
      tempOriginCountry &&
      !profile.raw_material_origins?.includes(tempOriginCountry)
    ) {
      setProfile({
        ...profile,
        raw_material_origins: [
          ...(profile.raw_material_origins || []),
          tempOriginCountry,
        ],
      });
      setTempOriginCountry('');
      setChangesSaved(false);
    }
  };

  return (
    <Modal show={show} centered size="xl" onHide={closeModal} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Build Your Public Profile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'general')}
            className="mb-4"
          >
            {/* TAB 1: GENERAL INFO */}
            <Tab eventKey="general" title="1. Company Info">
              <SupplierProfileBuilderGeneralTab
                profile={profile}
                setProfile={setProfile}
                errors={errors}
                isSaving={isSaving}
                setChangesSaved={setChangesSaved}
                onSave={handleSaveGeneral}
              />
            </Tab>

            {/* TAB 2: OPERATIONS */}
            <Tab eventKey="operations" title="2. Operations">
              <SupplierProfileBuilderOperationsTab
                operations={operations}
                setOperations={setOperations}
                profile={profile}
                setProfile={setProfile}
                opCountries={opCountries}
                countries={countries}
                tempCountry={tempCountry}
                setTempCountry={setTempCountry}
                errors={errors}
                isSaving={isSaving}
                addCountry={addCountry}
                removeCountry={removeCountry}
                onSave={saveOperations}
              />
            </Tab>

            {/* TAB 3 & 4: Certs and Policies (To be built next) */}
            <Tab eventKey="certifications" title="3. Certifications">
              <SupplierProfileBuilderCertifications supplierId={supplierId} />
            </Tab>
            <Tab eventKey="policies" title="4. Policies">
              <SupplierProfileBuilderPolicies supplierId={supplierId} />
            </Tab>
            {/* TAB 5: RESTRICTED DATA */}
            <Tab eventKey="kyb_aml" title="5. KYB & AML (Restricted)">
              <SupplierProfileBuilderRestrictedTab
                restrictedFields={restrictedFields}
                setRestrictedFields={setRestrictedFields}
                profile={profile}
                setProfile={setProfile}
                tempOriginCountry={tempOriginCountry}
                setTempOriginCountry={setTempOriginCountry}
                setChangesSaved={setChangesSaved}
                addOriginCountry={addOriginCountry}
                isSaving={isSaving}
                onSave={handleSaveRestricted}
                countries={countries}
              />
            </Tab>
            {/* <Tab eventKey="supply_chain_details" title="Supply Chain Details"></Tab> */}
          </Tabs>
        )}
      </Modal.Body>
      {showSuccessToast && (
        <ToastContainer position={gToastPosition} className="p-3">
          <Toast
            onClose={() => setShowSuccessToast(false)}
            show={showSuccessToast}
            delay={3000}
            autohide
            bg={gToastSentiment}
          >
            <Toast.Body>Company info saved successfully!</Toast.Body>
          </Toast>
        </ToastContainer>
      )}
    </Modal>
  );
};

export default SupplierProfileBuilder;
